import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHead, Btn, Card, Field, Toggle, Label, ImageUpload, AiSeoButton, SeoPreview } from "@/admin/ui";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

const SettingsAdmin = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <p className="text-espresso/50">Loading…</p>;
  const up = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const payload = { ...s, checkout_trust: (s.checkout_trust || []).map((t) => (t || "").trim()).filter(Boolean) };
    try { await api.put("/admin/settings", payload); toast.success("Saved"); } catch { toast.error("Failed"); }
  };

  const nav = s.nav || [];
  const setNav = (arr) => up("nav", arr);
  const brand = s.brand || {};
  const ann = s.announcement || {};
  const promo = s.promoted || {};
  const trust = s.checkout_trust || [];
  const setTrust = (arr) => up("checkout_trust", arr);

  return (
    <div className="max-w-2xl">
      <PageHead title="Store Settings" sub="Announcement, navigation, brand & socials.">
        <Btn onClick={save} testid="admin-save">Save</Btn>
      </PageHead>

      <Card className="mb-4">
        <h2 className="font-serif text-2xl mb-4">Announcement Bar</h2>
        <Toggle label="Enabled" value={ann.enabled} onChange={(v) => up("announcement", { ...ann, enabled: v })} />
        <Field label="Text" value={ann.text} onChange={(v) => up("announcement", { ...ann, text: v })} />
        <Field label="Link" value={ann.href} onChange={(v) => up("announcement", { ...ann, href: v })} />
      </Card>

      <Card className="mb-4">
        <h2 className="font-serif text-2xl mb-4">Promoted (campaign) link</h2>
        <Toggle label="Enabled" value={promo.enabled} onChange={(v) => up("promoted", { ...promo, enabled: v })} />
        <Field label="Label" value={promo.label} onChange={(v) => up("promoted", { ...promo, label: v })} />
        <Field label="Link" value={promo.href} onChange={(v) => up("promoted", { ...promo, href: v })} />
      </Card>

      <Card className="mb-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-serif text-2xl">Navigation</h2>
          <Btn variant="ghost" onClick={() => setNav([...nav, { label: "NEW", href: "/" }])}><Plus size={13} className="inline" /> Item</Btn>
        </div>
        {nav.map((n, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input value={n.label} onChange={(e) => setNav(nav.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="flex-1 bg-ivory border border-espresso/20 px-3 py-2 text-sm" />
            <input value={n.href} onChange={(e) => setNav(nav.map((x, j) => j === i ? { ...x, href: e.target.value } : x))} className="flex-1 bg-ivory border border-espresso/20 px-3 py-2 text-sm" />
            <button onClick={() => setNav(nav.filter((_, j) => j !== i))} className="text-wine"><Trash2 size={16} /></button>
          </div>
        ))}
      </Card>

      <Card className="mb-4">
        <h2 className="font-serif text-2xl mb-4">Brand</h2>
        <Field label="Name" value={brand.name} onChange={(v) => up("brand", { ...brand, name: v })} />
        <Field label="Tagline" value={brand.tagline} onChange={(v) => up("brand", { ...brand, tagline: v })} />
        <Field label="Address" value={brand.address} onChange={(v) => up("brand", { ...brand, address: v })} />
        <Field label="Positioning" value={brand.positioning} onChange={(v) => up("brand", { ...brand, positioning: v })} />
      </Card>

      <Card className="mb-4">
        <h2 className="font-serif text-2xl mb-4">Analytics</h2>
        <p className="text-xs text-espresso/50 mb-4">IDs load only after visitors consent. Leave blank to disable.</p>
        <Field testid="settings-ga-id" label="Google Analytics ID (G-XXXX)" value={s.analytics?.ga_id} onChange={(v) => up("analytics", { ...s.analytics, ga_id: v })} />
        <Field testid="settings-meta-pixel" label="Meta Pixel ID" value={s.analytics?.meta_pixel_id} onChange={(v) => up("analytics", { ...s.analytics, meta_pixel_id: v })} />
        <Field testid="settings-tiktok-pixel" label="TikTok Pixel ID" value={s.analytics?.tiktok_pixel_id} onChange={(v) => up("analytics", { ...s.analytics, tiktok_pixel_id: v })} />
      </Card>

      <Card className="mb-4">
        <h2 className="font-serif text-2xl mb-4">Site & Homepage SEO</h2>
        <p className="text-xs text-espresso/50 mb-4">Default title & description for your homepage and social shares.</p>
        <AiSeoButton subject={brand.name || "NoirbyAmara"} context={`${brand.tagline || ""} ${brand.positioning || ""}`} onResult={(d) => up("seo", { ...s.seo, title: d.title, description: d.description })} testid="settings-ai-seo" />
        <Field label="Default SEO Title" value={s.seo?.title} onChange={(v) => up("seo", { ...s.seo, title: v })} />
        <Field label="Default SEO Description" textarea value={s.seo?.description} onChange={(v) => up("seo", { ...s.seo, description: v })} />
        <SeoPreview title={s.seo?.title} description={s.seo?.description} path="/" />
      </Card>

      <Card className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <h2 className="font-serif text-2xl">Checkout trust</h2>
          <Btn variant="ghost" onClick={() => setTrust([...trust, ""])}><Plus size={13} className="inline" /> Line</Btn>
        </div>
        <p className="text-xs text-espresso/50 mb-4">Reassurance shown at checkout (secure payment, returns, delivery). Up to 3 lines. Use [to confirm] for anything not yet finalised. Leave empty to use safe defaults.</p>
        {trust.map((t, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input value={t} onChange={(e) => setTrust(trust.map((x, j) => j === i ? e.target.value : x))} data-testid={`settings-trust-${i}`}
              className="flex-1 bg-ivory border border-espresso/20 px-3 py-2 text-sm outline-none focus:border-espresso" />
            <button onClick={() => setTrust(trust.filter((_, j) => j !== i))} className="text-wine"><Trash2 size={16} /></button>
          </div>
        ))}
      </Card>

      <Card className="mb-4">
        <h2 className="font-serif text-2xl mb-2">Welcome offer (overseas visitors)</h2>
        <p className="text-xs text-espresso/50 mb-4">Shown in the one-time "shopping from abroad" note to international visitors. Create the matching code in <span className="text-espresso">Discounts</span> so it actually works at checkout.</p>
        <Toggle label="Enabled" value={s.welcome_offer_enabled} onChange={(v) => up("welcome_offer_enabled", v)} testid="settings-welcome-enabled" />
        <Field testid="settings-welcome-code" label="Code" value={s.welcome_offer_code} onChange={(v) => up("welcome_offer_code", (v || "").toUpperCase())} placeholder="e.g. WELCOME10" />
        <Field testid="settings-welcome-text" label="Message" value={s.welcome_offer_text} onChange={(v) => up("welcome_offer_text", v)} placeholder="e.g. Enjoy 10% off your first order." />
      </Card>

      <Card className="mb-4">
        <h2 className="font-serif text-2xl mb-2">Story film</h2>
        <p className="text-xs text-espresso/50 mb-4">A brand film that opens every product page before the stills — the story leads, then the shop. A product's own Opening film overrides this. Leave blank to show images first.</p>
        <Field label="Film video URL" value={s.story_film} onChange={(v) => up("story_film", v)} placeholder="/api/files/... or a hosted video URL" />
        <ImageUpload label="Film poster (still shown before it plays)" value={s.story_film_poster} onChange={(v) => up("story_film_poster", v)} />
      </Card>

      <Card>
        <h2 className="font-serif text-2xl mb-4">Socials</h2>
        <Field label="Instagram URL" value={s.socials?.instagram} onChange={(v) => up("socials", { ...s.socials, instagram: v })} />
        <Field label="TikTok URL" value={s.socials?.tiktok} onChange={(v) => up("socials", { ...s.socials, tiktok: v })} />
        <Field label="Pinterest URL" value={s.socials?.pinterest} onChange={(v) => up("socials", { ...s.socials, pinterest: v })} />
      </Card>
    </div>
  );
};

export default SettingsAdmin;
